import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import * as amplitude from "@amplitude/analytics-browser";
import { DataTable } from "@unified-checkout/design-system";
import type {
  IColumnsMap,
  IFilterMap,
  IFiltersMap
} from "libs/design-system/src/types/data_table";
import type { TUserActionModal } from "@app/dashboard/types/innerLayout/user_management/modals/actions";
import type { IDeleteModal } from "@app/dashboard/types/innerLayout/user_management/modals/delete-modal";
import type { IUserRoleModal } from "@app/dashboard/types/innerLayout/user_management/modals/user-role";
import {
  ArrowsRepeat,
  CircleExclamation,
  LinkSimple,
  PaperPlane,
  Pen,
  TrashCan
} from "@unified-checkout/design-system/icons/index";
import { useLangStore } from "@app/dashboard/store/lang";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import { SHARED_EVENTS_VALUES } from "@app/dashboard/constants/tracking_events/shared";
import {
  USER_AND_PERMISSIONS_EVENTS,
  USER_AND_PERMISSIONS_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/user-and-permissions";
import { SYSTEM_EVENTS_GROUPING_CODES } from "@app/dashboard/constants/tracking_events/system";
import { PROFILE } from "@app/dashboard/constants/profile";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { getUsers } from "@app/dashboard/services/inner_layout/user_management/users/get-users";
import { resendInvite } from "@app/dashboard/services/inner_layout/user_management/users/resend-invite";
import { getCountryFromDomain } from "@app/dashboard/utils/getCountryFromDomain";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import { getProfileType } from "@app/dashboard/utils/localStorageUtils";
import { notify } from "@app/dashboard/utils/notifier";
import useGetRolesList from "../../useGetRolesList";
import { UserRoleModal as ChangeRoleModal } from "../../components/modals/UserRoleModal";
import { UserRoleModal as UpdateExistingRoleModal } from "../../components/modals/UserRoleModal";
import { DeleteModal as RemoveMemberModal } from "../../components/modals/DeleteModal";
import { DeleteModal as CancelInvitationModal } from "../../components/modals/DeleteModal";
import { getToasterConfig } from "../../components/modals/utils";
import styles from "./users-and-permissions.module.scss";

const TABLE_ID = 'user_and_permissions_table';

export const UsersAndPermissions = () => {
  const { lang } = useLangStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const screenWidth = useScreenWidth();

  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const profileType = getProfileType();
  const isMerchant = profileType === PROFILE.TYPE.MERCHANT;

  type TObj = Record<string, string | number | boolean | null>;

  const [userRoleModalProps, setUserRoleModalProps] = useState<{
    isOpen: Record<Exclude<IUserRoleModal['type'], 'inviteMember'>, boolean>,
    id?: number,
    username?: string,
  }>({
    isOpen: {
      changeRole: false,
      updateExistingRole: false,
    },
  });

  const [deleteModalProps, setDeleteModalProps] = useState<{
    isOpen: Record<Exclude<IDeleteModal['type'], 'deleteRole'>, boolean>,
    email?: string,
    phoneNumber?: string,
    id?: number
  }>({
    isOpen: {
      removeMember: false,
      cancelInvitation: false,
    },
  });

  const handleModals = (action: Exclude<TUserActionModal, 'inviteMember'>, data: any) => {
    const { id, email, primary_phone_number, role, role_id, username } = data;

    const actions = {
      removeMember: () => {
        setDeleteModalProps((prevState) => ({
          isOpen: { ...prevState.isOpen, 'removeMember': true },
          email: email,
          id: id,
        }));
      },
      cancelInvitation: () => {
        setDeleteModalProps((prevState) => ({
          isOpen: { ...prevState.isOpen, 'cancelInvitation': true },
          email: email,
          phoneNumber: primary_phone_number,
          id: id,
        }));
      },
      changeRole: () => {
        setUserRoleModalProps((prevState) => ({
          isOpen: { ...prevState.isOpen, 'changeRole': true },
          id: id,
          username
        }));
      },
      updateExistingRole: () => {
        setUserRoleModalProps((prevState) => ({
          isOpen: { ...prevState.isOpen, 'updateExistingRole': true },
          id: id,
          username
        }));
      }
    };

    actions[action]();
  };

  const {
    isPending: isResendInvitePending,
    isError,
    mutate: mutateResendInvite
  } = useMutation({
    mutationKey: [QUERY_KEYS.RESEND_INVITE],
    mutationFn: (userId: string) => resendInvite(userId),
    onSuccess: () => {

      queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_USERS] });

      const successMsg = t('innerLayout.userManagementPage.toasters.resendLink.successText');
      const toasterConfig = getToasterConfig({
        type: 'success',
        msg: successMsg,
        trackingCode: SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.RESEND_INVITATION,
      });
      notify.success(successMsg, toasterConfig);
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const response = axiosErr?.response as any;
      const msg = response?.data?.message;

      const failureMsg = msg || t('innerLayout.userManagementPage.toasters.resendLink.failureText');
      const toasterConfig = getToasterConfig({
        type: 'error',
        msg: failureMsg,
        trackingCode: SYSTEM_EVENTS_GROUPING_CODES.USERS_AND_PERMS.RESEND_INVITATION,
      });
      notify.error(failureMsg, toasterConfig);
    },
  });

  const {
    rolesData,
    isLoading: isRolesResponseLoading,
    isError: isRolesResponseError
  } = useGetRolesList({ type: 'filters', lang });

  const rolesCount = rolesData?.map((role: any) => (role.user_count ?? null)) || [];
  const roles = rolesData?.map((role: any) => ({ [role.title]: role.id })) || [];

  const filtersMap: IFiltersMap = useMemo(() => {

    return rolesData.length ?
      [{
        type: 'multi_selection',
        key: 'role',
        name: t('innerLayout.userManagementPage.filters.role'),
        values: roles,
        defaultSelection: 'all',
        countList: rolesCount,
        multipleSelectionSummaryType: 'all',
        multiSelectionParamType: 'comma-separated'
      },
      {
        type: 'select',
        key: 'status',
        name: t('innerLayout.userManagementPage.filters.status'),
        values: [
          { [t('innerLayout.userManagementPage.filters.active')]: 'active' },
          { [t('innerLayout.userManagementPage.filters.pending')]: 'pending' },
          { [t('innerLayout.userManagementPage.filters.expired')]: 'expired' }
        ] as Record<string, string>[],
      },
      {
        type: 'search' as IFilterMap['type'],
        key: 'search_key',
        name: t('innerLayout.userManagementPage.filters.searchKey'),
        values: '',
        defaultSelection: '',
        placeHolder: t('innerLayout.userManagementPage.filters.searchByNameOrNumberOrEmail'),
        validations: [
          {
            regex: "/^.+$/",
            message: "Required"
          }
        ]
      }]
      :
      [];
  }, [t, rolesData]);

  const userPerms = {
    canCreateStaff: isAccessible(PERMISSIONS.CAN_CREATE_STAFF),
    canDeleteStaff: isAccessible(PERMISSIONS.CAN_DELETE_STAFF),
    canUpdateStaff: isAccessible(PERMISSIONS.CAN_UPDATE_STAFF),
  };

  const userStatuses: ('active' | 'pending' | "expired" | "action_required")[] =
    ['active', 'pending', 'action_required', 'expired'];

  const userActions = {
    'active': [
      ...(
        userPerms.canUpdateStaff ?
          [{
            icon: Pen,
            text: t('innerLayout.userManagementPage.table.actions.changeRole'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('changeRole', userObj),
            iconColor: "primary",
          }]
          :
          []
      ),
      ...(
        userPerms.canDeleteStaff ?
          [{
            icon: TrashCan,
            text: t('innerLayout.userManagementPage.table.actions.deleteUser'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('removeMember', userObj),
            iconColor: "danger",
          }]
          :
          []
      )
    ],
    'pending': [
      ...(
        userPerms.canCreateStaff ?
          [{
            icon: LinkSimple,
            text: t('innerLayout.userManagementPage.table.actions.resendLink'),
            onClickHandler: (_: string, userObj: TObj) => mutateResendInvite(userObj?.id?.toString() || ''),
            iconColor: "primary",
          }]
          :
          []
      ),
      ...(
        userPerms.canUpdateStaff ?
          [{
            icon: Pen,
            text: t('innerLayout.userManagementPage.table.actions.changeRole'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('changeRole', userObj),
            iconColor: "primary",
          }]
          :
          []
      ),
      ...(
        userPerms.canDeleteStaff ?
          [{
            icon: TrashCan,
            text: t('innerLayout.userManagementPage.table.actions.cancelInvitation'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('cancelInvitation', userObj),
            iconColor: "danger",
          }]
          :
          []
      )
    ],
    'expired': [
      ...(
        userPerms.canCreateStaff ?
          [{
            icon: PaperPlane,
            text: t('innerLayout.userManagementPage.table.actions.resendInvitation'),
            onClickHandler: (_: string, userObj: TObj) => mutateResendInvite(userObj?.id?.toString() || ''),
            iconColor: "primary",
          }]
          :
          []
      ),
      ...(
        userPerms.canUpdateStaff ?
          [{
            icon: Pen,
            text: t('innerLayout.userManagementPage.table.actions.changeRole'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('changeRole', userObj),
            iconColor: "primary",
          }]
          :
          []
      ),
      ...(
        userPerms.canDeleteStaff ?
          [{
            icon: TrashCan,
            text: t('innerLayout.userManagementPage.table.actions.cancelInvitation'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('cancelInvitation', userObj),
            iconColor: "danger",
          }]
          :
          []
      )
    ],
    'action_required': [
      ...(
        userPerms.canUpdateStaff ?
          [{
            icon: ArrowsRepeat,
            text:
              <>
                {t('innerLayout.userManagementPage.table.actions.updateRole')}
                <span className={styles['user-and-permissions__exclamation-warning-icon']}>{CircleExclamation}</span>
              </>,
            onClickHandler: (_: string, userObj: TObj) => handleModals('updateExistingRole', userObj),
            iconColor: "primary",
          }]
          :
          []
      ),
      ...(
        userPerms.canDeleteStaff ?
          [{
            icon: TrashCan,
            text: t('innerLayout.userManagementPage.table.actions.deleteUser'),
            onClickHandler: (_: string, userObj: TObj) => handleModals('removeMember', userObj),
            iconColor: "danger",
          }]
          :
          []
      )
    ],
  };

  const userActionsConditions = new Map(
    userStatuses.map((item) => [userActions[item], { 'status': item }])
  );

  const columnsMap: IColumnsMap = [
    {
      id: 'name',
      name: t('innerLayout.userManagementPage.table.headers.name'),
      type: 'text',
      key: "full_name",
      avatar: {
        conditionsType: 'location',
        conditions: {
          "img_url": { "img_url": /^https:/ },
        },
        default: 'full_name'
      }
    },
    {
      id: 'email',
      name: t('innerLayout.userManagementPage.table.headers.email'),
      type: 'text',
      key: "email",
    },
    {
      id: 'phone_number',
      name: t('innerLayout.userManagementPage.table.headers.phoneNumber'),
      type: 'text',
      key: "primary_phone_number",
    },
    {
      id: 'role',
      name: t('innerLayout.userManagementPage.table.headers.role'),
      type: 'text',
      key: "role",
    },
    {
      id: 'joined_on',
      name: t('innerLayout.userManagementPage.table.headers.joinedOn'),
      type: 'text',
      key: "joining_date.title",
      hintText: "joining_date.sub_title"
    },
    {
      id: 'status',
      name: t('innerLayout.userManagementPage.table.headers.status'),
      type: 'badge',
      key: "display_status",
      labelComparingValue: "status",
      label: {
        'active': { color: "green" },
        'expired': { color: "red" },
        'pending': { color: "yellow" },
        'action_required': { color: "yellow" },
      },
    },
    {
      id: 'dropdown',
      type: 'dropdown',
      name: '',
      key: {
        conditionsType: 'direct_value',
        conditions: !isMerchant ? {} : userActionsConditions,
        default: ""
      }
    }
  ];

  const deleteModalSharedProps:
    Omit<IDeleteModal, 'email' | 'type' | 'open' | 'setOpen'> = {
    userId: deleteModalProps.id || ''
  };

  const userRoleModalSharedProps:
    Omit<IUserRoleModal, 'type' | 'open' | 'setOpen'> = {
    id: userRoleModalProps.id as number,
    userName: userRoleModalProps.username,
  };

  const queryKeys = useMemo(
    () => [QUERY_KEYS.GET_USERS],
    []
  );

  const handleChangeRoleSuccess = (
    userId?: string | number,
    fromRole?: string,
    role?: string
  ) => {
    amplitude.track(USER_AND_PERMISSIONS_EVENTS.USER_ROLE_CHANGED, {
      [USER_AND_PERMISSIONS_EVENTS_PARAMS.TARGET_USER_ID]: userId,
      [USER_AND_PERMISSIONS_EVENTS_PARAMS.FROM_ROLE]: fromRole,
      [USER_AND_PERMISSIONS_EVENTS_PARAMS.TO_ROLE]: role,
      [USER_AND_PERMISSIONS_EVENTS_PARAMS.RESULT]: SHARED_EVENTS_VALUES.SUCCESS,
    });
  };

  return (
    <div className={styles["user-and-permissions"]}>
      <DataTable
        lang={lang}
        disabled={isResendInvitePending}

        filters={{
          filtersMap: filtersMap,
          isLoading: !filtersMap.length || isRolesResponseLoading,
          isMobile: isMobile,
          country: getCountryFromDomain(),
        }}

        tableBody={{
          lang: lang,
          tableId: TABLE_ID,
          dataLocation: "data.results",
          columnsMap: columnsMap
        }}

        pagination={{
          hideLabels: isMobile,
          type: "cursor_based",
          dataLocation: {
            next: "data.next",
            prev: "data.previous"
          },
          isMobile: isMobile
        }}

        queries={{
          queryKeys: queryKeys,
          queryFunction: getUsers,
        }}

        emptyState={{
          text: t('innerLayout.userManagementPage.emptyState.noUsers'),
          description: t('innerLayout.userManagementPage.emptyState.noUsersHint')
        }}
      />


      <ChangeRoleModal
        type="changeRole"
        {...userRoleModalSharedProps}
        open={userRoleModalProps.isOpen['changeRole']}
        setOpen={(open) => setUserRoleModalProps((prevState) => ({
          ...prevState,
          isOpen: { ...prevState.isOpen, 'changeRole': open }
        }))}
        onSuccessHandler={
          ({ userId, fromRole, role }) => handleChangeRoleSuccess(userId, fromRole, role)
        }
      />

      <UpdateExistingRoleModal
        type="updateExistingRole"
        {...userRoleModalSharedProps}
        open={userRoleModalProps.isOpen['updateExistingRole']}
        setOpen={(open) => setUserRoleModalProps((prevState) => ({
          ...prevState,
          isOpen: { ...prevState.isOpen, 'updateExistingRole': open }
        }))}
      />

      <RemoveMemberModal
        type='removeMember'
        {...deleteModalSharedProps}
        email={deleteModalProps.email || ''}
        open={deleteModalProps.isOpen['removeMember']}
        setOpen={(open) => setDeleteModalProps((prevState) => ({
          ...prevState,
          isOpen: { ...prevState.isOpen, 'removeMember': open }
        }))}
      />

      <CancelInvitationModal
        type='cancelInvitation'
        {...deleteModalSharedProps}
        open={deleteModalProps.isOpen['cancelInvitation']}
        email={deleteModalProps.email || ''}
        phoneNumber={deleteModalProps.phoneNumber || ''}
        setOpen={(open) => setDeleteModalProps((prevState) => ({
          ...prevState,
          isOpen: { ...prevState.isOpen, 'cancelInvitation': open }
        }))}
      />

    </div>
  )
}
